import {
  IQuoteItemWithDisplayType,
  IQuotePrice,
  IQuoteRespSchema,
  IRateResSchema,
} from '@monetize/types/app';
import { Control, Controller, useWatch } from 'react-hook-form';
import {
  getUpliftMultiplier,
  QuoteRenewalUpliftFormType,
} from '../../../utils/quoteRenewalUplift.utils';
import { applyUpliftToItem } from '../../../utils/upliftPrice.utils';
import {
  MCheckbox,
  MCustomNumberInput,
  MGridItem,
  MInputGroup,
  MText,
  MTooltip,
} from '../../Monetize';
import { PriceUpliftPpu } from '../PriceUpliftPpu';

type QuoteRenewalUpliftProductRowProps = {
  control: Control<QuoteRenewalUpliftFormType>;
  offeringIndex: number;
  productIndex: number;
  item: IQuoteItemWithDisplayType;
  quote: IQuoteRespSchema;
  quotePrices: IQuotePrice[];
  offeringRate?: IRateResSchema;
  parentQuoteOfferingId: string;
  /** Scopes the row's accessible name — the same product can sit on two offerings. */
  parentOfferingName: string;
  rateId: string;
  /** Product's `ppuDisplayPrecision`, when the catalog defines one. */
  displayPrecision?: number;
  isIndividualMode: boolean;
  isDisabled: boolean;
  /**
   * The offering's uplift is already committed to a generated renewal quote, so
   * its rows describe that configuration rather than the drawer's current mode.
   */
  isLocked: boolean;
  /** Uniform-mode percentage, applied to every product. */
  headerAmount: number | null;
};

export const QuoteRenewalUpliftProductRow = ({
  control,
  offeringIndex,
  productIndex,
  item,
  quote,
  quotePrices,
  offeringRate,
  parentQuoteOfferingId,
  parentOfferingName,
  rateId,
  displayPrecision,
  isIndividualMode,
  isDisabled,
  isLocked,
  headerAmount,
}: QuoteRenewalUpliftProductRowProps) => {
  const product = useWatch({
    control,
    name: `offerings.${offeringIndex}.products.${productIndex}`,
  });
  // A uniform uplift applies to every product, so the renewal price always
  // shows — inclusion is only selectable in individual mode. A locked offering
  // is the exception in both cases: the header percentage can never reach it, so
  // it keeps previewing the configuration it is already committed to.
  const isIncluded = isLocked
    ? !!product?.included
    : !isIndividualMode || !!product?.included;
  const effectiveAmount =
    isLocked || isIndividualMode ? product?.amount : headerAmount;

  return (
    <>
      {isIndividualMode && (
        <MGridItem>
          <Controller
            control={control}
            name={`offerings.${offeringIndex}.products.${productIndex}.included`}
            render={({ field: { value, onChange, ref, ...rest } }) => (
              <MCheckbox
                {...rest}
                ref={ref}
                isChecked={!!value}
                isDisabled={isDisabled}
                onChange={({ target }) => onChange(target.checked)}
                aria-label={`Include ${item.productName} on ${parentOfferingName} in renewal uplift`}
                data-testid={`renewal-uplift-product-checkbox-${parentQuoteOfferingId}-${item.productId}`}
              />
            )}
          />
        </MGridItem>
      )}

      <MGridItem minW={0}>
        <MTooltip label={item.productName}>
          <MText lineClamp={1}>{item.productName}</MText>
        </MTooltip>
      </MGridItem>

      {isIndividualMode && (
        <MGridItem>
          <Controller
            control={control}
            name={`offerings.${offeringIndex}.products.${productIndex}.amount`}
            render={({ field: { value, onChange, ...rest } }) => (
              <MInputGroup endElement={<MText fontWeight="bold">%</MText>}>
                <MCustomNumberInput
                  {...rest}
                  inputMode="decimal"
                  isDisabled={isDisabled || !isIncluded}
                  // Always show empty if checkbox is not checked
                  value={isIncluded ? (value ?? '') : ''}
                  onChange={(valueAsString, valueAsNumber) =>
                    onChange(valueAsString === '' ? null : valueAsNumber)
                  }
                  placeholder={isIncluded ? '0' : undefined}
                  styleProps={{
                    'aria-label': `Renewal uplift percentage for ${item.productName} on ${parentOfferingName}`,
                    'data-testid': `renewal-uplift-product-amount-input-${parentQuoteOfferingId}-${item.productId}`,
                  }}
                />
              </MInputGroup>
            )}
          />
        </MGridItem>
      )}

      <MGridItem>
        <PriceUpliftPpu
          item={item}
          quote={quote}
          quotePrices={quotePrices}
          offeringRate={offeringRate}
          productType={item.productType}
          parentQuoteOfferingId={parentQuoteOfferingId}
          rateId={rateId}
          displayPrecision={displayPrecision}
        />
      </MGridItem>

      {/* An excluded product renews at its current price, so the column is
          left empty rather than repeating it. */}
      <MGridItem>
        {isIncluded && (
          <PriceUpliftPpu
            item={applyUpliftToItem(item, getUpliftMultiplier(effectiveAmount))}
            quote={quote}
            quotePrices={quotePrices}
            offeringRate={offeringRate}
            productType={item.productType}
            parentQuoteOfferingId={parentQuoteOfferingId}
            rateId={rateId}
            displayPrecision={displayPrecision}
          />
        )}
      </MGridItem>
    </>
  );
};
